import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ThemeProvider, createTheme,
  Container, Typography, Box, Grid, CircularProgress, Button,
  Paper, InputBase, Slider, Select, MenuItem, FormControl,
  Divider, useMediaQuery, Chip, InputAdornment
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Info, Users, ChevronRight, X, Search, 
  Filter, DollarSign, ArrowUpDown, Wifi, Globe, Rss, Zap
} from 'lucide-react';

// Import components
import CircleCard from './CircleCard';
import Filters from './Filters';
import ComparisonDrawer from './ComparisonDrawer';
import './AllCircles.css';

// Import service
import { circleService } from '../../services/circleService';

// Enhanced theme with better color relationships
const allCirclesTheme = createTheme({
  palette: {
    primary: {
      main: '#FF4775',
      dark: '#E03060',
      light: '#FF6B90',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#3ACCE1',
      dark: '#25B1C5',
      light: '#5FD8EA',
      contrastText: '#FFFFFF'
    },
    background: {
      default: '#004C57',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#18272A',
      secondary: '#5A6D70',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, "Segoe UI", "Roboto", sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 700,
    },
    subtitle1: {
      fontWeight: 500,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          color: '#333',
          borderRadius: 12,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          color: '#333',
          '&.all-circles-select-input': {
            border: '1px solid rgba(229, 231, 235, 1)',
            outline: 'none',
            borderRadius: 12
          },
          '&:hover': {
            borderColor: 'rgba(209, 213, 219, 1)',
          },
          '&.Mui-focused': {
            borderColor: '#FF4775',
          },
        },
        notchedOutline: {
          border: 'none',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        thumb: {
          backgroundColor: '#FF4775',
          width: 16,
          height: 16,
          border: '2px solid white',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover, &.Mui-focusVisible': {
            boxShadow: '0 0 0 8px rgba(255, 71, 117, 0.16)',
          },
        },
        track: {
          background: 'linear-gradient(to right, #3ACCE1, #FF4775)',
          height: 4,
          borderRadius: 2,
          border: 'none',
        },
        rail: {
          height: 4,
          opacity: 0.4,
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
        },
        valueLabel: {
          backgroundColor: '#FF4775',
          color: 'white',
          fontSize: '0.75rem',
          padding: '2px 6px',
          borderRadius: '4px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 12,
          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
        },
        contained: {
          '&:hover': {
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          '@media (min-width: 600px)': {
            paddingLeft: 32,
            paddingRight: 32,
          },
        },
      },
    },
  },
});

// Enhanced Filter Component with improved UI/UX
const EnhancedFilters = ({ 
  searchTerm, setSearchTerm,
  selectedCarrier, setSelectedCarrier,
  selectedPlanType, setSelectedPlanType,
  sortBy, setSortBy,
  priceRange, setPriceRange,
  featuresFilter, setFeaturesFilter
}) => {
  const isMobile = useMediaQuery(allCirclesTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(allCirclesTheme.breakpoints.down('md'));
  
  // Features list with icons
  const featureOptions = [
    { value: 'unlimited_data', label: 'Unlimited Data', icon: <Wifi size={16} /> },
    { value: 'international', label: 'International', icon: <Globe size={16} /> },
    { value: 'hotspot', label: 'Hotspot', icon: <Rss size={16} /> },
    { value: '5g', label: '5G', icon: <Zap size={16} /> }
  ];
  
  // Toggle feature in filter
  const handleFeatureToggle = (feature) => {
    setFeaturesFilter(prev => 
      prev.includes(feature) 
        ? prev.filter(f => f !== feature) 
        : [...prev, feature]
    );
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Search and Filters Row */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr', md: '3fr 2fr 2fr' },
        gap: 2
      }}>
        {/* Enhanced Search */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: '50px',
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(200, 200, 200, 0.3)',
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            px: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            },
            '&:focus-within': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              borderColor: 'rgba(255, 71, 117, 0.5)',
            }
          }}
        >
          <Search size={18} color="#666" style={{ marginRight: 8 }} />

          <InputBase
            fullWidth
            placeholder="Search circles, carriers, plans..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiInputBase-input': {
                py: 1.2,
                fontSize: '0.9rem',
                fontWeight: 500,
                color: '#333',
              }
            }}
          />

          {searchTerm && (
            <Box 
              component="button"
              onClick={() => setSearchTerm('')}
              sx={{ 
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#999',
                display: 'flex',
                padding: '4px',
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              <X size={16} />
            </Box>
          )}
        </Box>

        {/* Carrier Filter - Redesigned */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: '50px',
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(200, 200, 200, 0.3)',
            height: '44px',
            overflow: 'hidden',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            }
          }}
        >
          <FormControl fullWidth sx={{ height: '100%' }}>
            <Select
              value={selectedCarrier}
              onChange={(e) => setSelectedCarrier(e.target.value)}
              displayEmpty
              sx={{
                height: '100%',
                color: '#333',
                fontWeight: 500,
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.2,
                  pl: 2,
                  pr: 2,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
              renderValue={(value) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Filter size={16} style={{ marginRight: '8px', color: '#666' }} />
                  {value === 'All' ? 'All Carriers' : value}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'white',
                    border: '1px solid rgba(200, 200, 200, 0.3)',
                    borderRadius: 2,
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                    '& .MuiMenuItem-root': {
                      padding: '8px 16px',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 71, 117, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 71, 117, 0.12)'
                        }
                      }
                    }
                  }
                }
              }}
            >
              <MenuItem value="All">All Carriers</MenuItem>
              <MenuItem value="Verizon">
                <Box 
                  component="span" 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    backgroundColor: '#FF0000',
                    display: 'inline-block',
                    mr: 1
                  }}
                />
                Verizon
              </MenuItem>
              <MenuItem value="AT&T">
                <Box 
                  component="span" 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    backgroundColor: '#0057B8',
                    display: 'inline-block',
                    mr: 1
                  }}
                />
                AT&T
              </MenuItem>
              <MenuItem value="T-Mobile">
                <Box 
                  component="span" 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    backgroundColor: '#E20074',
                    display: 'inline-block',
                    mr: 1
                  }}
                />
                T-Mobile
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Sort By - Redesigned */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: '50px',
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(200, 200, 200, 0.3)',
            height: '44px',
            overflow: 'hidden',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            }
          }}
        >
          <FormControl fullWidth sx={{ height: '100%' }}>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              displayEmpty
              sx={{
                height: '100%',
                color: '#333',
                fontWeight: 500,
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.2,
                  pl: 2,
                  pr: 2,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
              renderValue={(value) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ArrowUpDown size={16} style={{ marginRight: '8px', color: '#666' }} />
                  {value === 'price' ? 'Price: Low to High' : 
                  value === 'priceDesc' ? 'Price: High to Low' :
                  value === 'availability' ? 'Availability' : 
                  value === 'rating' ? 'Rating' : 
                  'Popularity'}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'white',
                    border: '1px solid rgba(200, 200, 200, 0.3)',
                    borderRadius: 2,
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                    '& .MuiMenuItem-root': {
                      padding: '8px 16px',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 71, 117, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 71, 117, 0.12)'
                        }
                      }
                    }
                  }
                }
              }}
            >
              <MenuItem value="price">Price: Low to High</MenuItem>
              <MenuItem value="priceDesc">Price: High to Low</MenuItem>
              <MenuItem value="availability">Availability</MenuItem>
              <MenuItem value="rating">Rating</MenuItem>
              <MenuItem value="popularity">Popularity</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Price Range Slider */}
      <Box sx={{ mt: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 1 
        }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'white',
              fontWeight: 600,
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <DollarSign size={16} />
            Price Range
          </Typography>
          <Box
            sx={{
              color: 'white',
              fontSize: '0.9rem',
              fontWeight: 600
            }}
          >
            ${priceRange[0]} - ${priceRange[1]}
          </Box>
        </Box>
        
        {/* Improved slider with discrete marks */}
        <Slider
          value={priceRange}
          onChange={(_, newValue) => setPriceRange(newValue)}
          min={0}
          max={100}
          step={5}
          valueLabelDisplay="off"
          aria-labelledby="price-range-slider"
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            height: 6,
            padding: '15px 0',
            '& .MuiSlider-thumb': {
              height: 20,
              width: 20,
              backgroundColor: '#fff',
              boxShadow: '0 0 0 2px currentColor',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.3)',
              },
              '&::before': {
                display: 'none',
              },
              '&::after': {
                display: 'none',
              },
              '&.Mui-active': { 
                boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.16)',
              }
            },
            '& .MuiSlider-rail': {
              opacity: 0.25,
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
            },
            '& .MuiSlider-track': {
              height: 6,
              border: 'none',
              backgroundImage: 'linear-gradient(90deg, rgba(58, 204, 225, 1), rgba(255, 71, 117, 0.8))',
              boxShadow: '0 0 6px rgba(58, 204, 225, 0.5)',
            },
            '& .MuiSlider-mark': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              height: 8,
              width: 1,
              marginTop: -3
            },
            '& .MuiSlider-markActive': {
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
            }
          }}
          marks={[
            { value: 0 },
            { value: 25 },
            { value: 50 },
            { value: 75 },
            { value: 100 }
          ]}
        />
      </Box>

      {/* Feature filters with better chip styling */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1.5, 
          mt: 1.5,
          justifyContent: 'flex-start'
        }}
      >
        {featureOptions.map(feature => (
          <Chip
            key={feature.value}
            icon={feature.icon}
            label={feature.label}
            onClick={() => handleFeatureToggle(feature.value)}
            sx={{
              height: 36,
              borderRadius: '18px',
              fontSize: '0.85rem',
              fontWeight: 600,
              backgroundColor: featuresFilter.includes(feature.value) 
                ? 'rgba(255, 255, 255, 0.25)' 
                : 'rgba(255, 255, 255, 0.15)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: featuresFilter.includes(feature.value) 
                  ? 'rgba(255, 255, 255, 0.3)' 
                  : 'rgba(255, 255, 255, 0.2)',
              },
              '& .MuiChip-icon': {
                color: 'white',
                marginLeft: '6px'
              },
              '& .MuiChip-label': {
                px: 1.5
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const AllCircles = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated, isLoading: authIsLoading } = useAuth0();
  const [circles, setCircles] = useState([]);
  const [filteredCircles, setFilteredCircles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState('All');
  const [selectedPlanType, setSelectedPlanType] = useState('All');
  const [sortBy, setSortBy] = useState('price');
  const [priceRange, setPriceRange] = useState([0, 100]);
  const [loading, setLoading] = useState(true);
  const [comparingCircles, setComparingCircles] = useState([]);
  const [isCompareDialogOpen, setIsCompareDialogOpen] = useState(false);
  const [featuresFilter, setFeaturesFilter] = useState([]);
  const [error, setError] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  
  // Track viewport size
  const isMobile = useMediaQuery(allCirclesTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(allCirclesTheme.breakpoints.down('md'));
  
  // Fix scroll behavior on mount
  useEffect(() => {
    document.body.style.overflow = 'visible';
    document.documentElement.style.overflow = 'visible';
    document.body.style.height = 'auto';
    document.documentElement.style.height = 'auto';
    document.body.style.touchAction = 'auto';
    document.body.style.position = 'static';
    
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.height = '';
      document.body.style.touchAction = '';
      document.body.style.position = '';
    };
  }, []);
  
  // Optimized scroll handler
  useEffect(() => {
    const handleScroll = () => {
      requestAnimationFrame(() => {
        setScrollY(window.scrollY);
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch circles data
  useEffect(() => {
    // Wait for Auth0 to resolve isAuthenticated before fetching, otherwise a
    // logged-in visitor fetches once as logged-out (false) then again once
    // Auth0 resolves (true), flashing the skeleton.
    if (authIsLoading) {
      return;
    }

    const fetchCircles = async () => {
      setLoading(true);
      try {
        // This page is public (Fix-first #1): only fetch an access token when
        // there is a logged-in session. A logged-out visitor still gets the
        // circle list and prices.
        let token;
        if (isAuthenticated) {
          token = await getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: 'openid profile email'
          });
        }

        // Use the circleService instead of direct fetch
        const response = await circleService.getCircles(token);
        const data = response.data;
        
        setCircles(data);
        setFilteredCircles(data);

        // Set initial filters from URL
        const carrierParam = searchParams.get('carrier');
        if (carrierParam) {
          setSelectedCarrier(decodeURIComponent(carrierParam));
        }
      } catch (err) {
        console.error('Error fetching circles:', err);
        // Use the friendlyMessage from our enhanced error handling
        setError(err.friendlyMessage || 'Failed to load circles. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCircles();
  }, [searchParams, getAccessTokenSilently, isAuthenticated, authIsLoading]);

  // Apply filters
  useEffect(() => {
    let result = circles;

    // Search filter
    if (searchTerm) {
      result = result.filter(circle =>
        circle.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        circle.carrier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        circle.plan_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Carrier filter
    if (selectedCarrier !== 'All') {
      result = result.filter(circle => circle.carrier_name === selectedCarrier);
    }

    // Plan type filter
    if (selectedPlanType !== 'All') {
      result = result.filter(circle => circle.plan_type === selectedPlanType);
    }

    // Feature filter
    if (featuresFilter.length > 0) {
      result = result.filter(circle =>
        featuresFilter.every(feature =>
          circle.features.some(f =>
            f.feature_type === feature
          )
        )
      );
    }

    // Price range filter
    result = result.filter(circle =>
      circle.monthly_payment_per_line >= priceRange[0] &&
      circle.monthly_payment_per_line <= priceRange[1]
    );

    // Sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a.monthly_payment_per_line - b.monthly_payment_per_line;
        case 'priceDesc':
          return b.monthly_payment_per_line - a.monthly_payment_per_line;
        case 'availability':
          return b.available_slots - a.available_slots;
        case 'rating':
          return (b.rating || 4) - (a.rating || 4);
        case 'popularity':
          return b.total_joiners - a.total_joiners;
        default:
          return 0;
      }
    });

    setFilteredCircles(result);

    // Update URL parameters
    const params = new URLSearchParams(searchParams);
    if (selectedCarrier !== 'All') {
      params.set('carrier', encodeURIComponent(selectedCarrier));
    } else {
      params.delete('carrier');
    }
    setSearchParams(params);
  }, [circles, searchTerm, selectedCarrier, selectedPlanType, sortBy, priceRange, featuresFilter, searchParams, setSearchParams]);

  // Compare circles
  const handleCompare = useCallback((circle) => {
    setComparingCircles(prev => {
      const circleIndex = prev.findIndex(c => c.id === circle.id);
      let newComparing;
      if (circleIndex !== -1) {
        newComparing = prev.filter(c => c.id !== circle.id);
      } else {
        newComparing = [...prev, circle].slice(-3); // Max 3
      }
      setIsCompareDialogOpen(newComparing.length > 1);
      return newComparing;
    });
  }, []);

  // Save/bookmark a circle
  const handleSave = useCallback((circle) => {
    setCircles(prev => prev.map(c =>
      c.id === circle.id ? { ...c, isSaved: !c.isSaved } : c
    ));
  }, []);

  // Navigate to circle creation
  const handleCreateCircle = () => {
    navigate('/onboarding');
  };

  // Error state
  if (error) {
    return (
      <ThemeProvider theme={allCirclesTheme}>
        <div className="all-circles-page" style={{ overflow: 'visible', touchAction: 'auto' }}>
          <Container maxWidth="lg">
            <Box 
              textAlign="center" 
              mt={6} 
              p={4} 
              borderRadius={4} 
              bgcolor="background.paper"
              boxShadow={2}
            >
              <Typography color="error" variant="h6" gutterBottom fontWeight={600}>
                {error}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.location.reload()}
                startIcon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                  </svg>
                }
                sx={{ mt: 2 }}
              >
                Try Again
              </Button>
            </Box>
          </Container>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={allCirclesTheme}>
      <div 
        className="all-circles-page" 
        style={{ 
          overflow: 'visible', 
          position: 'relative', 
          minHeight: '100vh',
          touchAction: 'auto',
          paddingBottom: '50px',
          zIndex: 1
        }}
      >
        {/* Optimized Background Elements - Modified to start below header */}
        <div 
          className="animated-background" 
          style={{ 
            position: 'fixed', 
            top: '60px', /* Start below header height */
            left: 0, 
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 0
          }}
        >
          <div 
            className="blob blob-1" 
            style={{ 
              transform: `translate(${scrollY * 0.02}px, ${scrollY * -0.006}px)`,
              willChange: 'transform',
              opacity: 0.4,
              filter: 'blur(80px)',
            }}
          />
          <div 
            className="blob blob-2" 
            style={{ 
              transform: `translate(${scrollY * -0.015}px, ${scrollY * 0.003}px)`,
              willChange: 'transform',
              opacity: 0.35,
              filter: 'blur(80px)',
            }}
          />
          <div 
            className="blob blob-3" 
            style={{ 
              transform: `translate(${scrollY * -0.008}px, ${scrollY * -0.02}px)`,
              willChange: 'transform',
              opacity: 0.35,
              filter: 'blur(80px)',
            }}
          />
          
          {/* Particle effects */}
          <div className="particle-container" style={{ pointerEvents: 'none' }}>
            {[...Array(20)].map((_, i) => (
              <div 
                key={i} 
                className="particle"
                style={{
                  width: `${Math.random() * 6 + 2}px`,
                  height: `${Math.random() * 6 + 2}px`,
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animationDuration: `${Math.random() * 20 + 10}s`,
                  animationDelay: `-${Math.random() * 10}s`,
                  pointerEvents: 'none',
                  opacity: Math.random() * 0.5 + 0.3,
                  boxShadow: '0 0 6px rgba(255, 255, 255, 0.3)',
                  background: Math.random() > 0.7 
                    ? 'rgba(255, 71, 117, 0.6)' 
                    : 'rgba(255, 255, 255, 0.6)',
                }}
              />
            ))}
          </div>
          
          {/* Subtle grid */}
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
              opacity: 0.4,
              pointerEvents: 'none'
            }}
          />
        </div>

        {/* Header Section */}
        <Box 
          className="all-circles-header-wrapper" 
          style={{ 
            position: 'relative', 
            zIndex: 1,
            opacity: Math.max(0, 1 - scrollY / 500),
            transform: `scale(${Math.max(0.96, 1 - scrollY / 2000)})`,
            transformOrigin: 'center top',
          }}
        >
          <Box 
            className="all-circles-header-content"
            maxWidth="xl"
            mx="auto"
            px={2}
          >
            <div style={{ height: '55px', width: '100%' }}></div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Typography 
                variant="h1" 
                className="all-circles-page-title"
                align="center"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                  background: 'linear-gradient(to right, #ffffff, #b3f0f5)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                  mb: 1,
                  mt: { xs: 8, sm: 8, md: 6 } // Increased margin-top for mobile/tablet
                }}
              >
                Join a Circle Today!
              </Typography>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Typography 
                variant="subtitle1" 
                className="all-circles-page-subtitle"
                align="center"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: { xs: 3, sm: 4 }
                }}
              >
                Connect with others and save on premium phone plans
              </Typography>
            </motion.div>
            
            {/* Enhanced Filters */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 3,
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                  mb: 4,
                  transform: `translateY(${Math.min(0, -scrollY * 0.1)}px)`,
                  maxWidth: 'xl',
                  mx: 'auto'
                }}
              >
                <EnhancedFilters
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  selectedCarrier={selectedCarrier}
                  setSelectedCarrier={setSelectedCarrier}
                  selectedPlanType={selectedPlanType}
                  setSelectedPlanType={setSelectedPlanType}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  priceRange={priceRange}
                  setPriceRange={setPriceRange}
                  featuresFilter={featuresFilter}
                  setFeaturesFilter={setFeaturesFilter}
                />
              </Paper>
            </motion.div>
          </Box>
        </Box>

        {/* Main Content */}
        <Container 
          maxWidth="xl" 
          className="all-circles-container" 
          sx={{
            position: 'relative',
            zIndex: 2, // Higher z-index for main content
            backgroundColor: 'background.paper',
            borderRadius: '24px 24px 0 0',
            boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.1)',
            pt: 4,
            pb: 6,
            mt: { xs: -3, sm: -5 },
            px: { xs: 2, sm: 3, md: 4 },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100vw',
              right: '-100vw',
              bottom: '0',
              backgroundColor: 'background.paper',
              zIndex: -1,
              borderRadius: '24px 24px 0 0',
            }
          }}
        >
          {/* Active filters */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              justifyContent: 'flex-end', 
              alignItems: 'center',
              mb: 3,
              gap: 1
            }}
          >
            {selectedCarrier !== 'All' && (
              <Chip 
                label={selectedCarrier}
                size="small"
                onDelete={() => setSelectedCarrier('All')}
                sx={{ 
                  fontWeight: 600,
                  backgroundColor: 
                    selectedCarrier === 'Verizon' ? 'rgba(255, 0, 0, 0.1)' :
                    selectedCarrier === 'AT&T' ? 'rgba(0, 87, 184, 0.1)' :
                    'rgba(226, 0, 116, 0.1)',
                  color:
                    selectedCarrier === 'Verizon' ? '#cc0000' :
                    selectedCarrier === 'AT&T' ? '#0057b8' :
                    '#e20074',
                }}
              />
            )}
            
            {searchTerm && (
              <Chip 
                label={`Search: ${searchTerm}`}
                size="small"
                onDelete={() => setSearchTerm('')}
                sx={{ fontWeight: 600 }}
              />
            )}
            
            {featuresFilter.map(feature => (
              <Chip 
                key={feature}
                label={feature.replace('_', ' ')}
                size="small"
                color="primary"
                onDelete={() => setFeaturesFilter(prev => prev.filter(f => f !== feature))}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Box>
          
          <AnimatePresence>
            {loading ? (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                flexDirection="column"
                py={8}
                gap={2}
              >
                <CircularProgress 
                  className="all-circles-circular-progress"
                  size={40}
                />
                <Typography color="text.secondary" variant="body1">
                  Loading available circles...
                </Typography>
              </Box>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                {/* UPDATED: Grid layout with improved spacing for neo-glassmorphic design */}
                <Grid 
                  container 
                  spacing={3}
                  sx={{ mb: 4 }}
                >
                  {filteredCircles.map((circle, index) => (
                    <Grid 
                      item 
                      xs={12} 
                      sm={12} 
                      md={6} 
                      lg={4} 
                      key={circle.id}
                      className="all-circles-grid-item"
                      sx={{
                        height: '100%',
                        display: 'flex', // Make the Grid item a flex container
                        '& > *': {     // Target all direct children
                          width: '100%', // Ensure child takes full width
                          transition: 'all 0.3s ease',
                        }
                      }}
                    >
                      <CircleCard
                        circle={circle}
                        onCompare={handleCompare}
                        onSave={handleSave}
                        isComparing={comparingCircles.some(c => c.id === circle.id)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            )}
          </AnimatePresence>

          {/* No Results Message - Enhanced */}
          {filteredCircles.length === 0 && !loading && (
            <Box 
              textAlign="center" 
              py={6}
              sx={{
                borderRadius: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                border: '1px dashed rgba(0, 0, 0, 0.1)',
                p: 4
              }}
            >
              <Typography variant="h5" gutterBottom fontWeight={700}>
                No circles found matching your criteria
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={3} maxWidth={600} mx="auto">
                Try adjusting your filters or create your own circle to start saving on your phone plan today.
              </Typography>
              <motion.div
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="contained"
                  startIcon={<Users size={20} />}
                  endIcon={<ChevronRight size={20} />}
                  size="large"
                  onClick={handleCreateCircle}
                  sx={{
                    background: 'linear-gradient(90deg, #3ACCE1, #FF4775)',
                    py: 1.5,
                    px: 3,
                    borderRadius: '16px',
                  }}
                >
                  Create Your Own Circle
                </Button>
              </motion.div>
            </Box>
          )}

          {/* Create Circle Button */}
          {filteredCircles.length > 0 && (
            <Box 
              mt={4} 
              textAlign="center"
              py={2}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="contained"
                  startIcon={<Users size={20} />}
                  endIcon={<ChevronRight size={20} />}
                  size="large"
                  onClick={handleCreateCircle}
                  sx={{
                    background: 'linear-gradient(90deg, #3ACCE1, #FF4775)',
                    borderRadius: '16px',
                    padding: '12px 24px',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      animation: 'shine 2s infinite',
                      '@keyframes shine': {
                        '0%': { left: '-100%' },
                        '50%': { left: '200%' },
                        '100%': { left: '200%' }
                      }
                    }
                  }}
                >
                  Create Your Own Circle
                </Button>
              </motion.div>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mt: 2, opacity: 0.7 }}
              >
                Start your own circle and save even more on your phone bill!
              </Typography>
            </Box>
          )}

          {/* Info Section - Enhanced */}
          <Box 
            mt={6}
            sx={{
              background: 'linear-gradient(120deg, #f8f9fa, #f1f3f5)',
              borderRadius: '20px',
              p: { xs: 3, sm: 4 },
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.03)',
              border: '1px solid #e9ecef',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                fontWeight: 700,
                color: 'text.primary',
                mb: 2
              }}
            >
              <Box 
                sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3ACCE1, #00B0C0)',
                  color: 'white',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Info size={20} />
              </Box>
              How Circles Work
            </Typography>
            
            <Typography 
              variant="body1"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.7,
                fontSize: '1rem',
                maxWidth: '80ch',
              }}
            >
              Circles allow you to join group plans and save money on your mobile service.
              You can join an existing circle or create your own. All circle owners are verified
              to ensure a safe and reliable experience.
            </Typography>
          </Box>

          {/* Comparison Drawer */}
          {comparingCircles.length > 1 && (
            <ComparisonDrawer
              circles={comparingCircles}
              onClose={() => {
                setComparingCircles([]);
                setIsCompareDialogOpen(false);
              }}
            />
          )}
        </Container>
      </div>
    </ThemeProvider>
  );
};

export default AllCircles;